import { Controller, type FieldPath, type FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useRequestOtp from '@/hooks/auth/use-request-otp';
import cn from '@/utils/merge-class-names';

interface Props<TFieldValues extends FieldValues> {
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  type?: 'sign-up' | 'sign-in';
  name: FieldPath<TFieldValues>;
  className?: string;
  inputClassName?: string;
}

export function OneTimeCodeField<TFV extends FieldValues = Record<string, never>>({
  id = 'oneTimeCode',
  disabled = false,
  placeholder,
  type,
  name,
  className,
  inputClassName,
}: Props<TFV>) {
  const { control, trigger, getValues } = useFormContext();
  const { t } = useTranslation();
  const { request, countdown, isPending } = useRequestOtp(type);

  async function handleOtpRequest() {
    const values = getValues();

    if (await trigger(['countryCode', 'phoneNumber'])) {
      return request({
        Phone: values.phoneNumber,
        TelCode: values.countryCode,
      });
    }
  }

  return (
    <div className={cn('relative', className)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <input
            type='text'
            id={id}
            disabled={disabled}
            inputMode='numeric'
            placeholder={placeholder}
            autoComplete='one-time-code'
            className={cn('input input-bordered w-full focus:input-primary focus:!outline-none', inputClassName)}
            {...field}
          />
        )}
      />

      {countdown <= 0 || countdown >= 60 ? (
        <button
          type='button'
          disabled={disabled || isPending}
          onClick={handleOtpRequest}
          className='btn btn-ghost absolute inset-y-0 end-0 text-primary'>
          {isPending ? <span className='loading loading-spinner' /> : t('auth.request-otp')}
        </button>
      ) : (
        <button
          type='button'
          disabled
          className='btn btn-ghost absolute inset-y-0 end-0'>
          {t('auth.retry-after-seconds', { seconds: countdown })}
        </button>
      )}
    </div>
  );
}
